import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, Student } from '../services/authService';

interface AuthContextType {
  student: Student | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<Student>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [student, setStudent] = useState<Student | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);
      
      // Check if token exists and is valid
      const token = authService.getToken();
      if (token) {
        const isValid = await authService.verifyToken();
        if (isValid) {
          const studentData = authService.getCurrentStudent();
          if (studentData) {
            setStudent(studentData);
            setIsAuthenticated(true);
          }
        } else {
          // Token is invalid, clear auth data
          authService.clearAuth();
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      authService.clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await authService.login({ email, password });
      
      if (response.success && response.data) {
        setStudent(response.data.student);
        setIsAuthenticated(true);
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      setStudent(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: any): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await authService.register(data);
      
      if (response.success && response.data) {
        setStudent(response.data.student);
        setIsAuthenticated(true);
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch (error: any) {
      setStudent(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setStudent(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<Student>): Promise<void> => {
    try {
      const updatedStudent = await authService.updateProfile(updates);
      setStudent(updatedStudent);
    } catch (error: any) {
      throw error;
    }
  };

  const refreshProfile = async (): Promise<void> => {
    try {
      const updatedStudent = await authService.getProfile();
      setStudent(updatedStudent);
    } catch (error: any) {
      console.error('Profile refresh error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    student,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 